import {useEffect,useMemo,useState} from 'react';

type Program={id:string;name:string;description:string;enabled:boolean;version:number;
  manualLocationCodes:string[];membershipProductIds:string[]};
type MembershipOption={id:string;name:string};
type Data={programs:Program[];membershipOptions:MembershipOption[]};
type Draft={id?:string;expectedVersion?:number;name:string;description:string;
  enabled:boolean;manualLocationCodes:string[];membershipProductIds:string[]};
const LOCATIONS=[{code:'WC',label:'West Chester'},
  {code:'PR',label:'Princeton'},{code:'YD',label:'Yardley'}];

/** One tenant-owned program, optionally filtered by location for manual staff
 * assignment. Linking membership definitions does NOT fake a membership sale. */
export function ReferralProgramsWorkspace({locationCode}:{locationCode:string}){
  const [programs,setPrograms]=useState<Program[]>([]);
  const [memberships,setMemberships]=useState<MembershipOption[]>([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [draft,setDraft]=useState<Draft|null>(null);
  const [search,setSearch]=useState('');
  const [saving,setSaving]=useState(false);
  const [notice,setNotice]=useState('');
  const refresh=async(signal?:AbortSignal)=>{
    const response=await fetch('/api/v1/staff/referral-programs?'+
      new URLSearchParams({location:locationCode}),{
      credentials:'same-origin',cache:'no-store',signal});
    const json=await response.json() as {ok:boolean;data?:Data};
    if(!response.ok||!json.ok||!json.data)
      throw Error(response.status===403?
        'You do not have access to Referral Programs.':
        'Referral Programs are unavailable.');
    if(!signal?.aborted){setPrograms(json.data.programs);
      setMemberships(json.data.membershipOptions);}
  };
  useEffect(()=>{
    const controller=new AbortController();
    setLoading(true);setError('');setDraft(null);
    void refresh(controller.signal).catch(e=>{
      if(!controller.signal.aborted)setError(e instanceof Error?e.message:
        'Referral Programs unavailable.');
    }).finally(()=>{if(!controller.signal.aborted)setLoading(false);});
    return ()=>controller.abort();
  },[locationCode]);
  const shown=useMemo(()=>programs.filter(p=>
    p.name.toLowerCase().includes(search.trim().toLowerCase())),[programs,search]);
  const create=()=>{setDraft({name:'',description:'',enabled:true,
    manualLocationCodes:[],membershipProductIds:[]});setNotice('');};
  const edit=(p:Program)=>{setDraft({id:p.id,expectedVersion:p.version,
    name:p.name,description:p.description,enabled:p.enabled,
    manualLocationCodes:[...p.manualLocationCodes],
    membershipProductIds:[...p.membershipProductIds]});setNotice('');};
  const toggle=(key:'manualLocationCodes'|'membershipProductIds',value:string)=>{
    setDraft(d=>d?{...d,[key]:d[key].includes(value)?
      d[key].filter(x=>x!==value):[...d[key],value]}:d);
  };
  const save=async()=>{
    if(!draft||saving)return;
    setSaving(true);setNotice('');
    try{
      const response=await fetch('/api/v1/staff/referral-programs',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:locationCode,...draft}),
      });
      const body=await response.json() as {ok:boolean;error?:{code:string}};
      if(!response.ok||!body.ok)
        throw Error(body.error?.code==='REFERRAL_PROGRAM_CHANGED'?
          'This program changed elsewhere. Reload and retry.':
          body.error?.code==='ACCESS_DENIED'?
            'Only authorized Setup staff can edit Referral Programs.':
            'Could not save referral program.');
      await refresh();
      setDraft(null);setNotice('Referral program saved.');
    }catch(e){setNotice(e instanceof Error?e.message:'Unable to save.');}
    finally{setSaving(false);}
  };
  return <section className="ecfs-source-setup" aria-label="Referral Programs setup">
    <header className="ecfs-products-heading"><h1>Referral Programs</h1></header>
    <p>Create programs for your organization, link qualifying memberships, and
      optionally restrict which locations offer manual enrollment.</p>
    {loading&&<p role="status">Loading referral programs…</p>}
    {error&&<p role="alert">{error}</p>}
    {!loading&&!error&&<>
      <label>Search Referral Programs <input type="search" value={search}
        onChange={e=>setSearch(e.target.value)}/></label>
      <button type="button" onClick={create}>Add Referral Program</button>
      {draft&&<form aria-label="Referral Program editor"
        className="ecfs-source-setup-editor" onSubmit={e=>{
        e.preventDefault();void save();
      }}>
        <h2>{draft.id?'Edit Referral Program':'New Referral Program'}</h2>
        <label>Program Name <input required maxLength={160} value={draft.name}
          onChange={e=>setDraft(d=>d?{...d,name:e.target.value}:d)}/></label>
        <label>Description <textarea maxLength={3000} value={draft.description}
          onChange={e=>setDraft(d=>d?{...d,description:e.target.value}:d)}/></label>
        <label><input type="checkbox" checked={draft.enabled}
          onChange={e=>setDraft(d=>d?{...d,enabled:e.target.checked}:d)}/>
          Enabled</label>
        <fieldset><legend>Manual enrollment visibility</legend>
          <p>Leave all locations unselected to permit staff enrollment at every location.</p>
          {LOCATIONS.map(l=><label key={l.code}>
            <input type="checkbox" checked={draft.manualLocationCodes.includes(l.code)}
              onChange={()=>toggle('manualLocationCodes',l.code)}/>{l.label}</label>)}
        </fieldset>
        <fieldset><legend>Automatically included with memberships</legend>
          <p>Link membership definitions. Enrollment and cancellation take effect
            when verified membership lifecycle events are available.</p>
          {memberships.length===0?<p>No memberships have been configured.</p>:
            memberships.map(m=><label key={m.id}>
              <input type="checkbox"
                checked={draft.membershipProductIds.includes(m.id)}
                onChange={()=>toggle('membershipProductIds',m.id)}/>{m.name}
            </label>)}
        </fieldset>
        <div className="ecfs-products-editor-actions">
          <button type="submit" disabled={saving}>
            {saving?'Saving…':'Save Referral Program'}</button>
          <button type="button" onClick={()=>setDraft(null)}
            disabled={saving}>Cancel</button>
        </div>
      </form>}
      {shown.length===0?<p>No matching referral programs.</p>:
        <table className="ecfs-record-table">
          <thead><tr><th>Status</th><th>Program</th><th>Manual Locations</th>
            <th>Linked Memberships</th><th>Setup</th></tr></thead>
          <tbody>{shown.map(p=><tr key={p.id}>
            <td>{p.enabled?'Enabled':'Disabled'}</td>
            <td><strong>{p.name}</strong>{p.description&&<small>
              {p.description}</small>}</td>
            <td>{p.manualLocationCodes.length?
              p.manualLocationCodes.join(', '):'All locations'}</td>
            <td>{p.membershipProductIds.length?
              memberships.filter(m=>p.membershipProductIds.includes(m.id))
                .map(m=>m.name).join(', '):'None'}</td>
            <td><button type="button" onClick={()=>edit(p)}>
              Edit</button></td>
          </tr>)}</tbody>
        </table>}
      {notice&&<p role="status">{notice}</p>}
    </>}
  </section>;
}
